import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { dashboardAPI, ordersAPI, tasksAPI } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { useOverdue } from '../../contexts/OverdueContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Progress } from '../../components/ui/progress';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area
} from 'recharts';
import { 
  ShoppingCart, Users, TrendingUp, 
  CheckSquare, Plus, ArrowRight, ArrowUp, ArrowDown,
  Wallet, Clock, AlertTriangle, Banknote, Package,
  FileText, Percent, UserCheck, BarChart3,
  CircleDollarSign, ListChecks, Boxes, AlertCircle,
  Crown, Repeat, CalendarDays, Minus
} from 'lucide-react';
import { formatCurrency, formatDate, ORDER_STATUSES, cn } from '../../lib/utils';
import { toast } from 'sonner';

const CHART_COLORS = ['#6366F1', '#F97316', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4'];
const PIE_COLORS = ['#6366F1', '#F97316', '#10B981', '#EF4444'];

// Russian pluralization: pluralize(5, 'задача', 'задачи', 'задач') => 'задач'
function pluralize(n, one, few, many) {
  const abs = Math.abs(n);
  const mod10 = abs % 10;
  const mod100 = abs % 100;
  if (mod100 >= 11 && mod100 <= 19) return many;
  if (mod10 === 1) return one;
  if (mod10 >= 2 && mod10 <= 4) return few;
  return many;
}

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [pendingTasks, setPendingTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const { overdueCount } = useOverdue();
  const { user } = useAuth();
  const location = useLocation();

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 6) return 'Доброй ночи';
    if (hour < 12) return 'Доброе утро';
    if (hour < 18) return 'Добрый день';
    return 'Добрый вечер';
  };

  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      const [statsRes, ordersRes, tasksRes] = await Promise.all([
        dashboardAPI.getStats(),
        ordersAPI.getAll({ limit: 5 }),
        tasksAPI.getAll({ status: 'pending' }),
      ]);

      setStats(statsRes.data);
      setRecentOrders(ordersRes.data.slice(0, 5));
      setPendingTasks(tasksRes.data.slice(0, 5));
    } catch (error) {
      toast.error('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  }, []);

  // Reload data every time user navigates to dashboard
  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData, location.key]);

  // Reload data when window regains focus (user switches back to tab)
  useEffect(() => {
    const handleFocus = () => {
      loadDashboardData();
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [loadDashboardData]);

  // Prepare chart data
  const monthlyRevenueData = stats?.monthly_revenue 
    ? (() => {
        // Collect all months from orders revenue, manual income, and expenses
        const allMonths = new Set([
          ...Object.keys(stats.monthly_revenue || {}),
          ...Object.keys(stats.monthly_manual_income || {}),
          ...Object.keys(stats.monthly_expense || {}),
        ]);
        return [...allMonths]
          .sort()
          .slice(-6)
          .map(month => ({
            month: new Date(month + '-01').toLocaleDateString('ru-RU', { month: 'short' }),
            revenue: (stats.monthly_revenue?.[month] || 0) + (stats.monthly_manual_income?.[month] || 0),
            orders: stats.monthly_orders?.[month] || 0,
            expense: stats.monthly_expense?.[month] || 0,
            profit: ((stats.monthly_revenue?.[month] || 0) + (stats.monthly_manual_income?.[month] || 0)) - (stats.monthly_expense?.[month] || 0),
          }));
      })()
    : [];

  const statusData = stats?.status_counts
    ? Object.entries(stats.status_counts).map(([status, count]) => ({
        name: ORDER_STATUSES[status]?.label || status,
        value: count,
      }))
    : [];

  const monthlyCustomersData = stats?.monthly_customers
    ? Object.entries(stats.monthly_customers)
        .sort(([a], [b]) => a.localeCompare(b))
        .slice(-6)
        .map(([month, value]) => ({
          month: new Date(month + '-01').toLocaleDateString('ru-RU', { month: 'short' }),
          customers: value,
        }))
    : [];

  const tasksPriorityData = stats?.tasks_by_priority
    ? [
        { name: 'Высокий', value: stats.tasks_by_priority.high || 0, color: '#EF4444' },
        { name: 'Средний', value: stats.tasks_by_priority.medium || 0, color: '#F59E0B' },
        { name: 'Низкий', value: stats.tasks_by_priority.low || 0, color: '#10B981' },
      ].filter(d => d.value > 0)
    : [];

  if (loading) {
    return <DashboardSkeleton />;
  }

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold tracking-tight text-foreground">
            {getGreeting()}{user?.name ? `, ${user.name.split(' ')[0]}` : ''}
          </h1>
          <p className="text-sm text-muted-foreground">
            Обзор вашего бизнеса
          </p>
        </div>
        {/* Action buttons — always one row */}
        <div className="flex items-center gap-2">
          <Link to="/orders?create=true" className="flex-1 sm:flex-none">
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white w-full sm:w-auto" data-testid="new-order-btn">
              <Plus className="h-3.5 w-3.5 mr-1" />
              Заказ
            </Button>
          </Link>
          <Link to="/tasks?create=true" className="flex-1 sm:flex-none">
            <Button size="sm" className="bg-violet-600 hover:bg-violet-700 text-white w-full sm:w-auto" data-testid="new-task-btn">
              <Plus className="h-3.5 w-3.5 mr-1" />
              Задача
            </Button>
          </Link>
          <Link to="/customers?create=true" className="flex-1 sm:flex-none">
            <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white w-full sm:w-auto" data-testid="new-customer-btn">
              <Plus className="h-3.5 w-3.5 mr-1" />
              Клиент
            </Button>
          </Link>
        </div>
      </div>

      {/* Overdue Tasks Alert Banner */}
      {overdueCount > 0 && (
        <Link to="/tasks" className="block mt-3">
          <div className="flex items-center gap-3 rounded-xl border border-destructive/40 bg-destructive/10 px-4 py-3 text-destructive hover:bg-destructive/15 transition-colors cursor-pointer"
            data-testid="overdue-banner"
          >
            <AlertTriangle className="h-5 w-5 shrink-0" />
            <div className="flex-1 min-w-0">
              <span className="font-semibold text-sm">
                Просрочено {overdueCount} {pluralize(overdueCount, 'задача', 'задачи', 'задач')}
              </span>
              <span className="text-xs text-destructive/70 ml-2 hidden sm:inline">
                — нажмите, чтобы открыть список
              </span>
            </div>
            <ArrowRight className="h-4 w-4 shrink-0" />
          </div>
        </Link>
      )}

      {/* Low stock materials alert */}
      {stats?.low_stock_materials > 0 && (
        <Link to="/materials" className="block mt-3">
          <div className="flex items-center gap-3 rounded-xl border border-amber-400/40 bg-amber-50 dark:bg-amber-950/20 px-4 py-3 text-amber-700 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-950/30 transition-colors cursor-pointer">
            <Boxes className="h-5 w-5 shrink-0" />
            <div className="flex-1 min-w-0">
              <span className="font-semibold text-sm">
                {stats.low_stock_materials} {pluralize(stats.low_stock_materials, 'материал', 'материала', 'материалов')} с низким остатком
              </span>
            </div>
            <ArrowRight className="h-4 w-4 shrink-0" />
          </div>
        </Link>
      )}

      {/* === ROW 1: Primary Financial Metrics (4 cards) === */}
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <MetricCard
          title="Прибыль"
          value={formatCurrency(stats?.net_profit || 0)}
          icon={TrendingUp}
          iconBg="bg-emerald-100 dark:bg-emerald-950/50"
          iconColor="text-emerald-600 dark:text-emerald-400"
          accent={(stats?.net_profit || 0) < 0 ? "border-red-200 dark:border-red-900/50" : "border-emerald-200 dark:border-emerald-900/50"}
          footer={<span className="text-[11px] text-muted-foreground">Доход − Расходы</span>}
        />
        <MetricCard
          title="Общий доход"
          value={formatCurrency(stats?.total_income)}
          icon={CircleDollarSign}
          iconBg="bg-emerald-100 dark:bg-emerald-950/50"
          iconColor="text-emerald-600 dark:text-emerald-400"
          footer={
            <span className="text-[11px] text-muted-foreground">
              Заказы: {formatCurrency(stats?.orders_income)}{stats?.manual_income > 0 ? ` · Ручные: ${formatCurrency(stats?.manual_income)}` : ''}
            </span>
          }
        />
        <MetricCard
          title="Расходы"
          value={formatCurrency(stats?.manual_expense || 0)}
          icon={Wallet}
          iconBg="bg-red-100 dark:bg-red-950/50"
          iconColor="text-red-600 dark:text-red-400"
          footer={<span className="text-[11px] text-muted-foreground">Из раздела «Финансы»</span>}
        />
        <MetricCard
          title="К получению"
          value={formatCurrency(stats?.total_remaining)}
          icon={Clock}
          iconBg="bg-amber-100 dark:bg-amber-950/50"
          iconColor="text-amber-600 dark:text-amber-400"
          footer={<span className="text-[11px] text-muted-foreground">Остаток по заказам</span>}
        />
      </div>

      {/* === ROW 2: Orders & Business Metrics (4 cards) === */}
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <MetricCard
          title="Заказы"
          value={stats?.total_orders || 0}
          icon={ShoppingCart}
          iconBg="bg-indigo-100 dark:bg-indigo-950/50"
          iconColor="text-indigo-600 dark:text-indigo-400"
          footer={
            <div className="flex items-center gap-1.5 flex-wrap">
              {stats?.new_orders > 0 && <Badge variant="secondary" className="text-[10px] h-4 px-1.5">Новых: {stats.new_orders}</Badge>}
              {stats?.in_progress_orders > 0 && <Badge variant="secondary" className="text-[10px] h-4 px-1.5">В работе: {stats.in_progress_orders}</Badge>}
            </div>
          }
        />
        <MetricCard
          title="Клиенты"
          value={stats?.customers_count || 0}
          icon={Users}
          iconBg="bg-pink-100 dark:bg-pink-950/50"
          iconColor="text-pink-600 dark:text-pink-400"
          footer={stats?.repeat_customers > 0 && (
            <span className="text-[11px] text-muted-foreground flex items-center gap-1"><Repeat className="h-3 w-3" /> Постоянных: {stats.repeat_customers}</span>
          )}
        />
        <MetricCard
          title="Предоплаты"
          value={formatCurrency(stats?.total_prepayment)}
          icon={Banknote}
          iconBg="bg-violet-100 dark:bg-violet-950/50"
          iconColor="text-violet-600 dark:text-violet-400"
          footer={<span className="text-[11px] text-muted-foreground">{stats?.prepayment_pct || 0}% от суммы активных заказов</span>}
        />
        <MetricCard
          title="Задачи"
          value={stats?.total_tasks || 0}
          icon={ListChecks}
          iconBg="bg-orange-100 dark:bg-orange-950/50"
          iconColor="text-orange-600 dark:text-orange-400"
          footer={
            <div className="flex items-center gap-1.5 flex-wrap">
              {stats?.overdue_tasks > 0 && <Badge variant="destructive" className="text-[10px] h-4 px-1.5">Просроч.: {stats.overdue_tasks}</Badge>}
              {stats?.in_progress_tasks > 0 && <Badge variant="secondary" className="text-[10px] h-4 px-1.5">В работе: {stats.in_progress_tasks}</Badge>}
            </div>
          }
        />
      </div>

      {/* === ROW 3: Progress bars === */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
        <ProgressCard
          title="Конверсия заказов"
          value={stats?.conversion_rate || 0}
          color="bg-emerald-500"
          description={`${stats?.completed_orders || 0} выполнено / ${stats?.cancelled_orders || 0} отменено`}
        />
        <ProgressCard
          title="Выполнение задач"
          value={stats?.task_completion_rate || 0}
          color="bg-blue-500"
          description={`${stats?.completed_tasks || 0} из ${stats?.total_tasks || 0} выполнено`}
        />
        <ProgressCard
          title="Сбор оплаты"
          value={stats?.prepayment_pct || 0}
          color="bg-violet-500"
          description={`${formatCurrency(stats?.total_prepayment)} из ${formatCurrency(stats?.active_orders_total)} (активные заказы)`}
        />
      </div>

      {/* === ROW 4: Charts === */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Revenue Chart */}
        <Card className="lg:col-span-2" data-testid="revenue-chart-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Доходы и расходы</CardTitle>
            <CardDescription>Динамика по месяцам (заказы + ручные доходы)</CardDescription>
          </CardHeader>
          <CardContent>
            {monthlyRevenueData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={monthlyRevenueData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
                  <Tooltip 
                    formatter={(value, name) => {
                      const labels = { revenue: 'Доход', expense: 'Расходы', orders: 'Заказы' };
                      return [name === 'orders' ? value : formatCurrency(value), labels[name] || name];
                    }}
                    contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px', fontSize: 12 }}
                  />
                  <Bar dataKey="revenue" fill="#10B981" radius={[4, 4, 0, 0]} name="revenue" />
                  <Bar dataKey="expense" fill="#EF4444" radius={[4, 4, 0, 0]} name="expense" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChartState message="Нет данных для отображения" />
            )}
          </CardContent>
        </Card>

        {/* Status Pie Chart */}
        <Card data-testid="status-chart-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Заказы по статусам</CardTitle>
            <CardDescription>Распределение</CardDescription>
          </CardHeader>
          <CardContent>
            {statusData.length > 0 ? (
              <div>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={statusData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {statusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 mt-2">
                  {statusData.map((entry, idx) => (
                    <div key={entry.name} className="flex items-center gap-1.5 text-xs">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: PIE_COLORS[idx % PIE_COLORS.length] }} />
                      <span className="text-muted-foreground">{entry.name}:</span>
                      <span className="font-semibold">{entry.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <EmptyChartState message="Нет заказов" />
            )}
          </CardContent>
        </Card>
      </div>

      {/* === ROW 4.5: Profit Chart === */}
      <div className="grid grid-cols-1 gap-4 sm:gap-6">
        <Card data-testid="profit-chart-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Прибыль по месяцам</CardTitle>
            <CardDescription>Доход − Расходы</CardDescription>
          </CardHeader>
          <CardContent>
            {monthlyRevenueData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={monthlyRevenueData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
                  <Tooltip
                    formatter={(value) => [formatCurrency(value), 'Прибыль']}
                    contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px', fontSize: 12 }}
                  />
                  <Bar
                    dataKey="profit"
                    name="profit"
                    radius={[4, 4, 0, 0]}
                    fill="#10B981"
                  >
                    {monthlyRevenueData.map((entry, index) => (
                      <Cell key={`profit-${index}`} fill={entry.profit >= 0 ? '#10B981' : '#EF4444'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChartState message="Нет данных для отображения" />
            )}
          </CardContent>
        </Card>
      </div>

      {/* === ROW 5: Secondary Charts === */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* New Customers Chart */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Новые клиенты</CardTitle>
            <CardDescription>По месяцам</CardDescription>
          </CardHeader>
          <CardContent>
            {monthlyCustomersData.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={monthlyCustomersData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} allowDecimals={false} />
                  <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px', fontSize: 12 }} />
                  <Area type="monotone" dataKey="customers" stroke="#EC4899" fill="#EC4899" fillOpacity={0.15} strokeWidth={2} name="Клиенты" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChartState message="Нет данных" />
            )}
          </CardContent>
        </Card>

        {/* Tasks by Priority */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Задачи по приоритету</CardTitle>
            <CardDescription>Активные задачи</CardDescription>
          </CardHeader>
          <CardContent>
            {tasksPriorityData.length > 0 ? (
              <div>
                <ResponsiveContainer width="100%" height={160}>
                  <PieChart>
                    <Pie data={tasksPriorityData} cx="50%" cy="50%" innerRadius={40} outerRadius={65} paddingAngle={3} dataKey="value">
                      {tasksPriorityData.map((entry, index) => (
                        <Cell key={`p-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex justify-center gap-4 mt-2">
                  {tasksPriorityData.map(d => (
                    <div key={d.name} className="flex items-center gap-1.5 text-xs">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: d.color }} />
                      <span className="text-muted-foreground">{d.name}:</span>
                      <span className="font-semibold">{d.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <EmptyChartState message="Нет активных задач" />
            )}
          </CardContent>
        </Card>

        {/* Top Customers */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base">Топ клиенты</CardTitle>
                <CardDescription>По сумме заказов</CardDescription>
              </div>
              <Crown className="h-4 w-4 text-amber-500" />
            </div>
          </CardHeader>
          <CardContent>
            {stats?.top_customers?.length > 0 ? (
              <div className="space-y-2.5">
                {stats.top_customers.map((c, idx) => (
                  <div key={c.id} className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className={cn(
                        "w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0",
                        idx === 0 ? "bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-400" :
                        idx === 1 ? "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300" :
                        "bg-orange-100 text-orange-600 dark:bg-orange-950/50 dark:text-orange-400"
                      )}>
                        {idx + 1}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{c.name}</p>
                        <p className="text-[10px] text-muted-foreground">{c.orders_count} {pluralize(c.orders_count, 'заказ', 'заказа', 'заказов')}</p>
                      </div>
                    </div>
                    <span className="text-sm font-bold text-nowrap">{formatCurrency(c.total)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center py-6 text-muted-foreground text-sm">
                <Users className="h-8 w-8 mb-2 opacity-20" />
                <p>Нет данных</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* === ROW 6: Quick Stats Row === */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
        <MiniStat icon={FileText} label="Форм" value={stats?.forms_count || 0} color="text-indigo-500" />
        <MiniStat icon={Package} label="Материалов" value={stats?.materials_count || 0} color="text-teal-500" />
        <MiniStat icon={CalendarDays} label="Дней анализа" value={stats?.period_days || 0} color="text-gray-500" />
        <MiniStat icon={Repeat} label="Пост. клиенты" value={stats?.repeat_customers || 0} color="text-pink-500" />
        <MiniStat icon={AlertCircle} label="Отменено" value={stats?.cancelled_orders || 0} color="text-red-500" />
        <MiniStat icon={Boxes} label="Мало на складе" value={stats?.low_stock_materials || 0} color={stats?.low_stock_materials > 0 ? "text-amber-500" : "text-green-500"} />
      </div>

      {/* === ROW 7: Recent Orders & Pending Tasks === */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Recent Orders */}
        <Card data-testid="recent-orders-card">
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <div>
              <CardTitle className="text-base">Последние заказы</CardTitle>
              <CardDescription>5 последних</CardDescription>
            </div>
            <Link to="/orders">
              <Button variant="ghost" size="sm" className="h-8">
                Все <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {recentOrders.length > 0 ? (
              <div className="space-y-2">
                {recentOrders.map((order) => (
                  <Link
                    key={order.id}
                    to={`/orders/${order.id}`}
                    className="flex flex-col sm:flex-row sm:items-center justify-between p-2.5 rounded-lg hover:bg-muted/60 transition-colors gap-1.5 sm:gap-2"
                    data-testid={`order-item-${order.id}`}
                  >
                    <div>
                      <p className="font-medium text-sm">Заказ {order.order_number_display || `№${order.order_number}`}</p>
                      <p className="text-[11px] text-muted-foreground">{formatDate(order.created_at)}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={cn("text-[10px]", ORDER_STATUSES[order.status]?.color)}>
                        {ORDER_STATUSES[order.status]?.label}
                      </Badge>
                      <span className="font-semibold text-sm">{formatCurrency(order.total)}</span>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <EmptyState 
                icon={ShoppingCart}
                title="Нет заказов"
                description="Создайте первый заказ"
                action={
                  <Link to="/orders?create=true">
                    <Button size="sm">Создать заказ</Button>
                  </Link>
                }
              />
            )}
          </CardContent>
        </Card>

        {/* Pending Tasks */}
        <Card data-testid="pending-tasks-card">
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <div>
              <CardTitle className="flex items-center gap-2 text-base">
                Ожидающие задачи
                {overdueCount > 0 && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-destructive/15 border border-destructive/30 px-2 py-0.5 text-[10px] font-bold text-destructive">
                    <AlertTriangle className="h-2.5 w-2.5" />
                    {overdueCount}
                  </span>
                )}
              </CardTitle>
              <CardDescription>{stats?.pending_tasks || 0} задач в ожидании</CardDescription>
            </div>
            <Link to="/tasks">
              <Button variant="ghost" size="sm" className="h-8">
                Все <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {pendingTasks.length > 0 ? (
              <div className="space-y-1.5">
                {pendingTasks.map((task) => {
                  const isOverdue = task.due_date && new Date(task.due_date) < new Date() && task.status !== 'completed';
                  return (
                    <Link
                      key={task.id}
                      to={`/tasks?highlight=${task.id}`}
                      className={cn(
                        "flex items-center justify-between p-2.5 rounded-lg hover:bg-muted/60 transition-colors",
                        isOverdue && "bg-destructive/5 hover:bg-destructive/10 border border-destructive/20"
                      )}
                      data-testid={`task-item-${task.id}`}
                    >
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div className={cn(
                          "h-6 w-6 rounded-full flex items-center justify-center shrink-0",
                          isOverdue ? "bg-destructive/20 text-destructive" : "bg-muted text-muted-foreground"
                        )}>
                          {isOverdue
                            ? <Clock className="h-3 w-3" />
                            : <CheckSquare className="h-3 w-3" />
                          }
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-sm truncate">{task.title}</p>
                          {task.due_date && (
                            <p className={cn(
                              "text-[11px]",
                              isOverdue ? "text-destructive font-medium" : "text-muted-foreground"
                            )}>
                              {isOverdue ? '⚠ ' : ''}{formatDate(task.due_date)}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0 ml-2">
                        {isOverdue && <Badge variant="destructive" className="text-[10px] px-1.5 py-0 h-4">!</Badge>}
                        <Badge variant={task.priority === 'high' ? 'destructive' : 'secondary'} className="text-[10px] px-1.5 py-0 h-4">
                          {task.priority === 'high' ? '🔴' : task.priority === 'medium' ? '🟡' : '🟢'}
                        </Badge>
                      </div>
                    </Link>
                  );
                })}
              </div>
            ) : (
              <EmptyState 
                icon={CheckSquare}
                title="Нет задач"
                description="Все задачи выполнены"
              />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ===== COMPONENTS =====

function MetricCard({ title, value, icon: Icon, iconBg, iconColor, accent, footer }) {
  return (
    <Card className={accent || ''}>
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <p className="text-[11px] sm:text-xs font-medium text-muted-foreground uppercase tracking-wide">{title}</p>
            <p className="text-lg sm:text-2xl font-bold mt-1 truncate">{value}</p>
          </div>
          <div className={cn("p-2 sm:p-2.5 rounded-xl flex-shrink-0", iconBg)}>
            <Icon className={cn("h-4 w-4 sm:h-5 sm:w-5", iconColor)} />
          </div>
        </div>
        {footer && <div className="mt-2">{footer}</div>}
      </CardContent>
    </Card>
  );
}

function GrowthBadge({ value }) {
  if (!value || value === 0) return null;
  const isPositive = value > 0;
  return (
    <span className={cn(
      "inline-flex items-center gap-0.5 text-[11px] font-semibold",
      isPositive ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"
    )}>
      {isPositive ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
      {Math.abs(value)}% к пред. мес.
    </span>
  );
}

function ProgressCard({ title, value, color, description }) {
  return (
    <Card>
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-muted-foreground">{title}</span>
          <span className="text-sm font-bold">{value}%</span>
        </div>
        <Progress value={value} className="h-2" indicatorClassName={color} />
        <p className="text-[10px] text-muted-foreground mt-1.5">{description}</p>
      </CardContent>
    </Card>
  );
}

function MiniStat({ icon: Icon, label, value, color }) {
  return (
    <Card>
      <CardContent className="p-3 flex items-center gap-2.5">
        <Icon className={cn("h-4 w-4 flex-shrink-0", color)} />
        <div className="min-w-0">
          <div className="text-base font-bold leading-tight">{value}</div>
          <div className="text-[10px] text-muted-foreground truncate">{label}</div>
        </div>
      </CardContent>
    </Card>
  );
}

function EmptyState({ icon: Icon, title, description, action }) {
  return (
    <div className="flex flex-col items-center justify-center py-8 text-center">
      <Icon className="h-10 w-10 text-muted-foreground/50 mb-3" />
      <p className="font-medium text-sm">{title}</p>
      <p className="text-xs text-muted-foreground">{description}</p>
      {action && <div className="mt-3">{action}</div>}
    </div>
  );
}

function EmptyChartState({ message }) {
  return (
    <div className="flex flex-col items-center justify-center h-[200px] text-muted-foreground">
      <BarChart3 className="h-8 w-8 mb-2 opacity-20" />
      <p className="text-sm">{message}</p>
    </div>
  );
}

function DashboardSkeleton() {
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-10 w-32" />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28" />)}
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28" />)}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Skeleton className="h-80 lg:col-span-2" />
        <Skeleton className="h-80" />
      </div>
    </div>
  );
}
